import { memo, useMemo } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { ChartWrapper } from './ChartWrapper';
import { DarkTooltip } from './Tooltip';
import { CHART_CONFIG, CHART_DATA_CONFIG, CHART_THEMES, ANALYTICS_CONFIG } from '../config';
import {
  downsampleData,
  shouldDownsample,
  getOptimalMaxPoints,
} from '../../../utils/chartDownsampling';
import { useResponsiveChartConfig } from './useResponsiveChartConfig';

interface BaseBarChartProps<T> {
  data: T[];
  height?: number;
  getTitle: (item: T) => string;
  getValue: (item: T) => number;
  titleMaxLength?: number;
  metricLabel?: string;
  onBarClick?: (item: T) => void;
  colorScheme?: string[];
}

function truncateText(text: string, maxLength: number): string {
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
}

export const BaseBarChart = memo(function BaseBarChart<T>({
  data,
  height = 280,
  getTitle,
  getValue,
  titleMaxLength = 15,
  metricLabel = ANALYTICS_CONFIG.METRIC_LABELS.SESSIONS,
  onBarClick,
  colorScheme = [...CHART_DATA_CONFIG.BAR_COLORS],
}: BaseBarChartProps<T>) {
  const { isMobile, getMargins } = useResponsiveChartConfig();
  const { displayData, originalIndices } = useMemo(() => {
    if (shouldDownsample(data.length, 50)) {
      const maxBars = Math.min(getOptimalMaxPoints(data.length), 50);
      const downsampled = downsampleData(data, maxBars, getValue);
      const indices = downsampled.map(item => data.indexOf(item));
      return { displayData: downsampled, originalIndices: indices };
    }
    return { displayData: data, originalIndices: data.map((_, index) => index) };
  }, [data, getValue]);

  const chartData = useMemo(() => {
    return displayData.map((item, index) => ({
      id: `item-${index}`,
      label: truncateText(getTitle(item), titleMaxLength),
      value: getValue(item),
      itemIndex: index,
      originalTitle: getTitle(item),
    }));
  }, [displayData, getTitle, getValue, titleMaxLength]);

  const CustomTooltip = ({ data }: { data: { value: number; originalTitle: string } }) => (
    <DarkTooltip>
      <div className="text-center">
        <div className="mb-2 text-base font-semibold">{data.originalTitle}</div>
        <div className="text-sm text-[var(--anl-text-muted)]">
          {metricLabel}: <span className="font-semibold text-[var(--anl-text)]">{data.value}</span>
        </div>
      </div>
    </DarkTooltip>
  );

  const handleClick = useMemo(() => {
    return onBarClick
      ? (datum: { data: { itemIndex: number } }) => {
          const originalIndex = originalIndices[datum.data.itemIndex];
          const originalItem = data[originalIndex];
          if (originalItem) {
            onBarClick(originalItem);
          }
        }
      : undefined;
  }, [onBarClick, data, originalIndices]);

  if (!data?.length) {
    return (
      <div
        className="flex h-full items-center justify-center text-[var(--anl-text-muted)]"
        style={{ height }}
      >
        <p className="text-sm">No data available</p>
      </div>
    );
  }

  const extendedMargins = {
    ...getMargins('BAR_CHART'),
    bottom: isMobile ? 65 : 90,
    left: isMobile ? 50 : 80,
    right: 30,
  };

  return (
    <ChartWrapper height={height}>
      <ResponsiveBar
        data={chartData}
        keys={['value']}
        indexBy="label"
        onClick={handleClick}
        colors={colorScheme}
        margin={extendedMargins}
        padding={CHART_CONFIG.PADDING}
        valueScale={{ type: 'linear' }}
        indexScale={{ type: 'band', round: true }}
        borderColor={{
          from: 'color',
          modifiers: [['darker', 1.6]],
        }}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: CHART_CONFIG.TICK_SIZE,
          tickPadding: 8,
          tickRotation: CHART_CONFIG.TICK_ROTATION,
          legend: '',
          legendPosition: 'middle',
          legendOffset: 32,
        }}
        axisLeft={{
          tickSize: CHART_CONFIG.TICK_SIZE,
          tickPadding: CHART_CONFIG.TICK_PADDING,
          tickRotation: 0,
          legend: '',
          legendPosition: 'middle',
          legendOffset: CHART_CONFIG.LEGEND_OFFSET,
        }}
        enableLabel={false}
        animate={true}
        theme={CHART_THEMES.default}
        tooltip={CustomTooltip}
      />
    </ChartWrapper>
  );
}) as <T>(props: BaseBarChartProps<T>) => JSX.Element;
